// Admin routes of otto-app (design 9.10; Wes's Sept 28 2026 list): Otto
// Studio (every config kind with versions, activate, roll back), consults
// list and replay, live now, switches and caps, quality scorecard, uptime.
// Diffs between versions are computed in the browser from two GETs.
//
// Every route here requires an admin session. The sign-in method is Wes's
// decision D2 (Cloudflare Access or emailed links), never a shared password.
import { z } from "zod";
import {
  CoachFlag,
  ConfigKind,
  ConfigPins,
  ConfigVersionMeta,
  ModelPrice,
  RuntimeSettings,
  RuntimeSettingsPatch,
  Switches,
  SwitchesPatch,
} from "@otto/config";
import { ConsultationPackage, TestKind, TranscriptMessage } from "@otto/package-schema";
import { EndReason, Mode } from "@otto/protocol";
import { LedgerTimelineEntry, OttoNoteRecord } from "./brain";
import { ConsultIdParams, IsoTime, Ok, route } from "./common";
import { ConsultStatus, Industry, Language } from "./public";

// ---------------------------------------------------------------------------
// Otto Studio: config versions
// ---------------------------------------------------------------------------

export const KindParams = z.object({ kind: ConfigKind });
export const KindVersionParams = z.object({ kind: ConfigKind, version: z.coerce.number().int().min(1) });

export const ConfigVersionListResponse = z.object({
  ok: Ok,
  kind: ConfigKind,
  active_version: z.number().int().min(1).nullable(),
  /** Newest first. */
  versions: z.array(ConfigVersionMeta),
});

export const ConfigVersionResponse = z.object({
  ok: Ok,
  meta: ConfigVersionMeta,
  /** Validate in the browser with ConfigBodySchemas[kind] from @otto/config. */
  body: z.unknown(),
});

/** Saving always creates a NEW draft version; versions are immutable. */
export const CreateConfigVersionRequest = z.strictObject({
  body: z.unknown(),
  note: z.string().max(1000).nullable(),
  /** The version this edit started from, for the history view. */
  based_on_version: z.number().int().min(1).nullable(),
});

/**
 * Activate a version. otto-app refuses (422, code "invalid_config", with
 * issues) when the bundle it would make does not load. New consults use it;
 * consults already running keep the bundle they started with.
 */
export const ActivateRequest = z.strictObject({ version: z.number().int().min(1) });

/** Roll back to `to_version`, or to the previously active version when omitted. */
export const RollbackRequest = z.strictObject({ to_version: z.number().int().min(1).optional() });

export const ActivationResponse = z.object({
  ok: Ok,
  kind: ConfigKind,
  active_version: z.number().int().min(1),
  previous_active_version: z.number().int().min(1).nullable(),
});

// ---------------------------------------------------------------------------
// Consults list and replay
// ---------------------------------------------------------------------------

const BoolParam = z.enum(["true", "false"]).transform((v) => v === "true");

export const ConsultListQuery = z.object({
  from: IsoTime.optional(),
  to: IsoTime.optional(),
  status: ConsultStatus.optional(),
  mode: Mode.optional(),
  language: Language.optional(),
  channel: z.string().max(64).optional(),
  /** Tests are excluded unless asked for (design 8.1). */
  include_tests: BoolParam.optional(),
  min_rating: z.coerce.number().int().min(1).max(5).optional(),
  /** The check-in pick; "Other" matches every free-text industry. */
  industry: Industry.optional(),
  /** How it ended, or "not_ended" for consults still open. */
  end_reason: z.union([EndReason, z.literal("not_ended")]).optional(),
  /** MUST coverage at least this share (0 to 1). */
  min_must_coverage: z.coerce.number().min(0).max(1).optional(),
  has_hard_gate_failures: BoolParam.optional(),
  /** Only test consults of this kind (implies include_tests). */
  test_kind: TestKind.optional(),
  /** Name, business or email contains. */
  q: z.string().max(200).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const ConsultSummary = z.object({
  consult_id: z.uuid(),
  created_at: IsoTime,
  status: ConsultStatus,
  end_reason: EndReason.nullable(),
  mode: Mode,
  language: Language,
  channel: z.string(),
  industry: z.string().nullable(),
  first_name: z.string(),
  business_name: z.string(),
  connected_minutes: z.number().min(0).nullable(),
  /** null = not priced; never shown as $0. */
  cost_usd: z.number().min(0).nullable(),
  reply_p50_ms: z.number().min(0).nullable(),
  must_coverage: z.number().min(0).max(1).nullable(),
  hard_gate_failures: z.number().int().min(0),
  rating: z.number().int().min(1).max(5).nullable(),
  is_test: z.boolean(),
});
export type ConsultSummary = z.infer<typeof ConsultSummary>;

export const ConsultListResponse = z.object({
  ok: Ok,
  items: z.array(ConsultSummary),
  next_cursor: z.string().nullable(),
});

export const ConsultEventView = z.object({
  id: z.string(),
  at: IsoTime,
  source: z.enum(["live", "app", "web", "admin", "queue", "cron"]),
  type: z.string().max(64),
  /** Counts and ids only; never owner text. */
  detail: z.record(z.string(), z.unknown()).nullable(),
});

export const TurnMetricView = z.object({
  turn: z.number().int().min(0),
  /** Transcript index of Otto's line for this turn; null when unknown or no words were saved. */
  message_i: z.number().int().min(0).nullable(),
  at: IsoTime,
  reply_latency_ms_do: z.number().min(0).nullable(),
  reply_latency_ms_browser: z.number().min(0).nullable(),
  visible_reaction_ms: z.number().min(0).nullable(),
  interrupted: z.boolean(),
  self_interrupted: z.boolean(),
  note_sent: z.boolean(),
  session_swap: z.boolean(),
});

export const CostLineView = z.object({
  at: IsoTime,
  vendor: z.string(),
  model: z.string().nullable(),
  line: z.enum(["voice", "coach", "drafter", "writer", "judge", "other"]),
  tokens_in: z.number().int().min(0).nullable(),
  tokens_out: z.number().int().min(0).nullable(),
  cost_usd: z.number().min(0).nullable(),
});

/**
 * The consult's scorecard (design 10.1): the code parts scored by
 * @otto/guard's scoreConsult in the package job, and the judge's parts once
 * the judge ships. Every missing number is null, never 0.
 */
export const ScorecardView = z.object({
  scorecard_version: z.string().max(64),
  scored_at: IsoTime,
  /** 0 to 100; null until every code and judged part has a score. */
  total: z.number().min(0).max(100).nullable(),
  /** false on any hard gate; null when the total is unknown and no gate failed. */
  passed: z.boolean().nullable(),
  code_total: z.number().min(0).nullable(),
  code_max: z.number().min(0),
  dimensions: z.array(
    z.object({
      id: z.string().max(64),
      label: z.string().max(120),
      points: z.number().min(0),
      code_points: z.number().min(0),
      judge_points: z.number().min(0),
      code_score: z.number().min(0).nullable(),
      judge_score: z.number().min(0).nullable(),
      notes: z.array(z.string().max(500)).max(20),
    }),
  ),
  hard_gates: z.array(
    z.object({
      gate: z.string().max(64),
      rule_id: z.string().max(64).nullable(),
      i: z.number().int().min(0).nullable(),
      detail: z.string().max(500),
      source: z.enum(["guard", "scorecard", "judge"]),
    }),
  ),
  judge_rubric_version: z.string().max(64).nullable(),
});
export type ScorecardView = z.infer<typeof ScorecardView>;

/** Whether the package reached the webhook (design 8.3), from the packages table. */
export const HandoffView = z.object({
  state: z.enum(["off", "pending", "accepted", "failed", "dead", "skipped"]),
  attempts: z.number().int().min(0),
  accepted_at: IsoTime.nullable(),
  last_error: z.string().max(500).nullable(),
  /** The outbox row a resend acts on; null when nothing was queued (the switch was off, or the consult was held). */
  outbox_id: z.string().nullable(),
});

export const ConsultDetailResponse = z.object({
  ok: Ok,
  consult: ConsultSummary.extend({
    full_name: z.string(),
    email: z.string(),
    phone_e164: z.string(),
    industry_other: z.string().nullable(),
    source_code: z.string().nullable(),
    started_at: IsoTime.nullable(),
    ended_at: IsoTime.nullable(),
    config_pins: ConfigPins,
  }),
  transcript: z.array(TranscriptMessage),
  package: ConsultationPackage.nullable(),
  events: z.array(ConsultEventView),
  meter: z.object({
    cost_lines: z.array(CostLineView),
    turn_metrics: z.array(TurnMetricView),
  }),
  rating: z.object({ score: z.number().int().min(1).max(5), comment: z.string().nullable(), at: IsoTime }).nullable(),
  scorecard: ScorecardView.nullable(),
  /** Every coach hint, clock note, draft and cue sent to Otto (or dropped), in order. */
  coach_notes: z.array(OttoNoteRecord),
  /** How the information map filled up during the call. */
  ledger_timeline: z.array(LedgerTimelineEntry),
  /** null until a package exists. */
  handoff: HandoffView.nullable(),
});

// ---------------------------------------------------------------------------
// Settings, switches, live now, quality, uptime
// ---------------------------------------------------------------------------

export const SettingsResponse = z.object({ ok: Ok, settings: RuntimeSettings, updated_at: IsoTime.nullable() });

/**
 * Send whole sections you change; only the sections present are written, and
 * nothing is filled with defaults (RuntimeSettingsPatch in @otto/config).
 */
export const SettingsPatchRequest = RuntimeSettingsPatch;

export const SwitchesResponse = z.object({
  ok: Ok,
  switches: Switches,
  source: z.record(z.string(), z.enum(["setting", "env", "default"])),
  issues: z.array(z.string()),
});

/** Only the switches named in `set` change; nothing else is written or defaulted. */
export const SwitchesPatchRequest = z.strictObject({ set: SwitchesPatch });

export const LiveConsultView = z.object({
  consult_id: z.uuid(),
  started_at: IsoTime,
  clock_seconds: z.number().int().min(0),
  stage: z.string(),
  agenda_index: z.number().int().min(0),
  mode: Mode,
  channel: z.string(),
  cost_so_far_usd: z.number().min(0).nullable(),
  flags: z.array(CoachFlag),
  last_seen_at: IsoTime,
});

export const LiveNowResponse = z.object({
  ok: Ok,
  items: z.array(LiveConsultView),
  concurrency_cap: z.number().int().min(0),
  spend_today_usd: z.number().min(0).nullable(),
  daily_spend_cap_usd: z.number().min(0),
  drain: z.boolean(),
});

export const QualityQuery = z.object({
  from: IsoTime.optional(),
  to: IsoTime.optional(),
  include_tests: BoolParam.optional(),
});

/** The scorecard (design 10.1, 10.5). Every number is null when there is no data, never 0. */
export const QualitySummaryResponse = z.object({
  ok: Ok,
  from: IsoTime,
  to: IsoTime,
  consults: z.number().int().min(0),
  completed: z.number().int().min(0),
  completion_rate: z.number().min(0).max(1).nullable(),
  median_connected_minutes: z.number().min(0).nullable(),
  reply_latency_p50_ms: z.number().min(0).nullable(),
  reply_latency_p90_ms: z.number().min(0).nullable(),
  visible_reaction_p90_ms: z.number().min(0).nullable(),
  must_coverage_avg: z.number().min(0).max(1).nullable(),
  hard_gate_failures: z.number().int().min(0),
  avg_rating: z.number().min(1).max(5).nullable(),
  ratings: z.number().int().min(0),
  extension_offered: z.number().int().min(0),
  extension_accepted: z.number().int().min(0),
  cost_per_consult_median_usd: z.number().min(0).nullable(),
  by_mode: z.object({ video: z.number().int().min(0), voice: z.number().int().min(0) }),
  /** Average code score per scorecard dimension over scored consults; null with no data. */
  dimension_avgs: z.array(
    z.object({
      id: z.string().max(64),
      label: z.string().max(120),
      code_points: z.number().min(0),
      avg: z.number().min(0).nullable(),
      consults: z.number().int().min(0),
    }),
  ),
  /** The same numbers per set of pinned config versions, newest first. */
  by_version: z.array(
    z.object({
      pins: ConfigPins,
      consults: z.number().int().min(0),
      completed: z.number().int().min(0),
      /** Average scorecard total (null until the judge scores). */
      score_avg: z.number().min(0).max(100).nullable(),
      /** Average share of the code points earned, 0 to 1. */
      code_share_avg: z.number().min(0).max(1).nullable(),
      hard_gate_failures: z.number().int().min(0),
      must_coverage_avg: z.number().min(0).max(1).nullable(),
    }),
  ),
});

export const UptimeCheckView = z.object({
  target: z.string().max(200),
  checked_at: IsoTime,
  ok: z.boolean(),
  status_code: z.number().int().nullable(),
  latency_ms: z.number().min(0).nullable(),
  detail: z.string().max(500).nullable(),
});

export const UptimeQuery = z.object({ hours: z.coerce.number().int().min(1).max(24 * 30).default(24) });

export const UptimeResponse = z.object({
  ok: Ok,
  targets: z.array(
    z.object({
      target: z.string(),
      last: UptimeCheckView.nullable(),
      ok_share: z.number().min(0).max(1).nullable(),
    }),
  ),
  checks: z.array(UptimeCheckView),
});

/** End a live call from Live now. Otto gets the close note; the end still waits for his last word. */
export const ConsultEndRequest = z.strictObject({ note: z.string().max(500).optional() });
export const ConsultEndResponse = z.object({ ok: Ok, ended: z.boolean() });

/** The price table (dollars per million tokens), entered by a person. Empty until Wes enters prices. */
export const PricesResponse = z.object({ ok: Ok, prices: z.record(z.string(), ModelPrice) });
export const PricePatchRequest = z.strictObject({
  model: z.string().min(1).max(100),
  /** null makes the model unpriced (unknown) again. */
  price: ModelPrice.nullable(),
});

export const OutboxIdParams = z.object({ id: z.string().min(1).max(100) });

export const adminRoutes = {
  configVersions: route({
    method: "GET",
    path: "/api/admin/config/:kind",
    auth: "admin",
    params: KindParams,
    response: ConfigVersionListResponse,
  }),
  configVersion: route({
    method: "GET",
    path: "/api/admin/config/:kind/:version",
    auth: "admin",
    params: KindVersionParams,
    response: ConfigVersionResponse,
  }),
  createConfigVersion: route({
    method: "POST",
    path: "/api/admin/config/:kind",
    auth: "admin",
    params: KindParams,
    body: CreateConfigVersionRequest,
    response: ConfigVersionResponse,
  }),
  activateConfig: route({
    method: "POST",
    path: "/api/admin/config/:kind/activate",
    auth: "admin",
    params: KindParams,
    body: ActivateRequest,
    response: ActivationResponse,
  }),
  rollbackConfig: route({
    method: "POST",
    path: "/api/admin/config/:kind/rollback",
    auth: "admin",
    params: KindParams,
    body: RollbackRequest,
    response: ActivationResponse,
  }),
  consults: route({ method: "GET", path: "/api/admin/consults", auth: "admin", query: ConsultListQuery, response: ConsultListResponse }),
  consult: route({
    method: "GET",
    path: "/api/admin/consults/:id",
    auth: "admin",
    params: ConsultIdParams,
    response: ConsultDetailResponse,
  }),
  settings: route({ method: "GET", path: "/api/admin/settings", auth: "admin", response: SettingsResponse }),
  updateSettings: route({ method: "POST", path: "/api/admin/settings", auth: "admin", body: SettingsPatchRequest, response: SettingsResponse }),
  switches: route({ method: "GET", path: "/api/admin/switches", auth: "admin", response: SwitchesResponse }),
  updateSwitches: route({ method: "POST", path: "/api/admin/switches", auth: "admin", body: SwitchesPatchRequest, response: SwitchesResponse }),
  liveNow: route({ method: "GET", path: "/api/admin/live", auth: "admin", response: LiveNowResponse }),
  quality: route({ method: "GET", path: "/api/admin/quality", auth: "admin", query: QualityQuery, response: QualitySummaryResponse }),
  uptime: route({ method: "GET", path: "/api/admin/uptime", auth: "admin", query: UptimeQuery, response: UptimeResponse }),
  endConsult: route({
    method: "POST",
    path: "/api/admin/consults/:id/end",
    auth: "admin",
    params: ConsultIdParams,
    body: ConsultEndRequest,
    response: ConsultEndResponse,
  }),
  prices: route({ method: "GET", path: "/api/admin/prices", auth: "admin", response: PricesResponse }),
  updatePrice: route({ method: "POST", path: "/api/admin/prices", auth: "admin", body: PricePatchRequest, response: PricesResponse }),
  resendHandoff: route({
    method: "POST",
    path: "/api/admin/outbox/:id/resend",
    auth: "admin",
    params: OutboxIdParams,
    response: z.object({ ok: Ok }),
  }),
  /**
   * Build the package again for a consult whose package job failed (review
   * fix, Sept 28 2026: the queue never revives a failed consult on its own,
   * so a failure that always recurs cannot loop; only a person restarts it).
   */
  repackageConsult: route({
    method: "POST",
    path: "/api/admin/consults/:id/repackage",
    auth: "admin",
    params: ConsultIdParams,
    response: z.object({ ok: Ok }),
  }),
} as const;
